import { __decorate, __metadata } from "tslib";
// NPM
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import EcTextEditor from "@/components/widgets/EcTextEditor.vue";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
import { ActionItemModel, ActionItemStatus } from "@/store/models/clientUpdates/actionItem";
import { ClientUpdateModel } from "@/store/models/clientUpdates/clientUpdate";
import { IUpdatesTimelineEntryDTO } from "@/store/models/clientUpdates/updatesTimelineEntry";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import { toaster } from "@/utilities/toaster";
import { RECHECK_MODE } from "@/types/validators";
import { ACTION_ITEM_MAX_LENGTH, actionItemKey, byNewestActionItem, canModifyActionItem, formatDate, htmlToText, isAfterToday, isBeforeDay, isBeforeToday, NOT_OWNER_OR_ASSIGNEE_HINT, NOTES_LABEL, CLOSED_ACTION_ITEM_HINT, sameId, startOfToday, toInputDate, UPDATE_SIDE_CLIENT_ID, UPDATE_SIDE_DEVELOPMENT_ID, } from "./updatesHelpers";
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ["pdf", "docx", "xlsx", "png", "jpg", "jpeg"];
let AddUpdateModal = /** @class */ (() => {
    var _a, _b, _c;
    let AddUpdateModal = class AddUpdateModal extends Mixins(BaseMixer, StrictDateMixin, UnsavedChangesMixin) {
        constructor() {
            super(...arguments);
            this.clientSideId = UPDATE_SIDE_CLIENT_ID;
            this.developmentSideId = UPDATE_SIDE_DEVELOPMENT_ID;
            this.toolbarItems = ["bold", "italic", "underline", "|", "bulletedList", "numberedList"];
            this.fileAccept = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg";
            this.actionItemMaxLength = ACTION_ITEM_MAX_LENGTH;
            this.entryDate = "";
            this.sideId = UPDATE_SIDE_CLIENT_ID;
            // "" = Notes; anything else is a List Manager type. Every type has the same editor, action items and links.
            this.interactionTypeId = "";
            // "Include in Updates only" - the update is left out of the Weekly Standup
            this.isUpdatesOnly = false;
            this.bodyHtml = "";
            this.extrasTab = "actionItems";
            this.actionItemRows = [];
            // Which action item is showing its form; the rest stay in view mode
            this.editingActionKey = null;
            this.linkRows = [];
            // Existing items removed with the ✕ - deleted only when the update is saved
            this.deletedActionItemIds = [];
            this.deletedAttachmentIds = [];
            this.rowKey = 0;
            this.isBeforeToday = isBeforeToday;
            // Silent until the first Save, then each message clears as its field is fixed
            this.recheckMode = RECHECK_MODE;
            this.notOwnerHint = NOT_OWNER_OR_ASSIGNEE_HINT;
            this.assigneeLockedHint = "The assignee of a completed action item can't be changed.";
            this.duplicateRowMessage = "This Action Item is already assigned to the same person.";
            this.dueInPastMessage = "Due Date can't be earlier than today.";
            // Set the moment a valid save is clicked, before the machine reports `saving`
            this.submitting = false;
        }
        get isEdit() {
            return this.mode === "edit";
        }
        get isBusy() {
            return this.saving || this.submitting;
        }
        // Cleared once the save finishes - on failure the form stays open for another try
        onSavingChanged(value) {
            if (!value) {
                this.submitting = false;
            }
        }
        // Update Date: no future days and nothing before the client's Start Date
        disableEntryDate(date) {
            return isAfterToday(date) || isBeforeDay(date, this.clientStartDate);
        }
        // Rows that hold data. Every action item row counts: its fields are all mandatory, so a blank one must be
        // filled in or removed. Blank link rows are still dropped on save.
        get filledExtras() {
            return {
                actionItems: this.actionItemRows,
                links: this.linkRows.filter((x) => !!x.id || !!x.file || x.url.trim() !== ""),
            };
        }
        get isNotes() {
            return this.interactionTypeId === "";
        }
        get isDevelopment() {
            return sameId(this.sideId, UPDATE_SIDE_DEVELOPMENT_ID);
        }
        get typeOptions() {
            return [{ id: "", description: NOTES_LABEL }, ...(this.interactionTypes || [])];
        }
        get richPlaceholder() {
            if (this.isNotes) {
                return this.isDevelopment ? "Team work completed, in progress or blocked…" : "What was discussed or agreed with the client?";
            }
            return this.isDevelopment ? "What was discussed or agreed with the team?" : "What was discussed or agreed with the client?";
        }
        get richHasText() {
            return htmlToText(this.bodyHtml) !== "";
        }
        // The rich editor has no v-model, so its text is validated through a hidden input
        get richText() {
            return htmlToText(this.bodyHtml);
        }
        // Also catches an older update whose date predates a Start Date set later
        get entryBeforeClientStart() {
            return isBeforeDay(this.entryDate, this.clientStartDate);
        }
        get entryBeforeClientStartMessage() {
            return `Date can't be earlier than the client's Start Date (${formatDate(this.clientStartDate)}).`;
        }
        // Action item rows
        // Existing items the user may not change (Bug 16), and closed items (reopened on the Action Items page only),
        // stay in view mode with no edit/remove
        isRowLocked(row) {
            return !!row.original && (this.wasClosed(row) || !canModifyActionItem(row.original));
        }
        lockedRowHint(row) {
            return this.wasClosed(row) ? CLOSED_ACTION_ITEM_HINT : NOT_OWNER_OR_ASSIGNEE_HINT;
        }
        wasClosed(row) {
            return !!row.original && row.original.status === ActionItemStatus.Closed;
        }
        // A completed item keeps its assignee - unless it's being reopened in this save (reopen runs before the edit)
        isAssigneeLocked(row) {
            return this.wasCompleted(row) && row.completed;
        }
        isRowOpen(row) {
            return !row.completed && !row.closing && !this.wasClosed(row);
        }
        // Close (ellipsis menu): a saved Open item that isn't ticked Completed in this edit
        canClose(row) {
            return !!row.original && !this.isRowLocked(row) && row.original.status === ActionItemStatus.Open && !row.completed;
        }
        // Marks / unmarks the row for closing; nothing is sent until Save Update
        toggleClosing(row) {
            if (!this.canClose(row)) {
                return;
            }
            if (this.editingActionKey === row.key) {
                this.editingActionKey = null;
            }
            row.closing = !row.closing;
        }
        wasCompleted(row) {
            return !!row.original && row.original.status === ActionItemStatus.Completed;
        }
        // Only items already saved can be ticked - a new row is added first, then completed from the timeline or here
        canToggleCompleted(row) {
            return !!row.original && !this.isRowLocked(row) && !row.closing;
        }
        toggleCompleted(row) {
            if (this.canToggleCompleted(row)) {
                row.completed = !row.completed;
            }
        }
        // New or re-dated items can't be due in the past; an existing overdue item saved unchanged is fine
        isDueInPast(row) {
            return !!row.dueDate && (!row.original || row.dueDate !== toInputDate(row.original.dueDate)) && isBeforeDay(row.dueDate, startOfToday());
        }
        // Same text and assignee as another open row (Bug 18). Only new rows and rows whose text or assignee
        // changed are flagged, so older duplicates don't block saving the update. Clashes with items on other
        // updates are caught by the server.
        isDuplicateRow(row) {
            if (!this.isRowOpen(row) || row.description.trim() === "" || !row.assigneeUserId) {
                return false;
            }
            const original = row.original;
            if (original && row.description.trim() === (original.description || "").trim() && sameId(row.assigneeUserId, original.assigneeUserId)) {
                return false;
            }
            const key = actionItemKey(row.description, row.assigneeUserId);
            return this.actionItemRows.some((other) => other.key !== row.key && this.isRowOpen(other) && actionItemKey(other.description, other.assigneeUserId) === key);
        }
        // Mirrors the row's vee-validate rules - used on Save to find a row in view mode that still needs fixing
        isRowInvalid(row) {
            return row.description.trim() === "" || !row.assigneeUserId || !row.dueDate || this.isDueInPast(row) || this.isDuplicateRow(row) || !!this.dateError("due-" + row.key);
        }
        get bodyMissing() {
            return !this.richHasText;
        }
        // The row whose form is open, while it still needs fixing - Add Action Item and Done wait for it
        get isOpenRowInvalid() {
            const open = this.actionItemRows.find((row) => row.key === this.editingActionKey);
            return !!open && this.isRowInvalid(open);
        }
        // Save stays disabled until everything Save checks is met
        get canSave() {
            return (!this.isBusy &&
                !!this.entryDate &&
                !this.entryBeforeClientStart &&
                !this.hasDateErrors &&
                !this.bodyMissing &&
                !this.actionItemRows.some((row) => this.isRowInvalid(row)));
        }
        // Eligible assignees, plus any existing item's current assignee who is no longer eligible
        get assigneeOptions() {
            const options = (ClientUpdatesModule.eligibleAssignees || []).map((x) => ({ id: x.id.toString(), value: x.value }));
            this.actionItemRows.forEach((row) => {
                const original = row.original;
                if (original && original.assigneeUserId && !options.some((o) => sameId(o.id, original.assigneeUserId))) {
                    options.push({ id: original.assigneeUserId.toString(), value: original.assigneeName || "" });
                }
            });
            return options;
        }
        get attachmentRowCount() {
            return this.linkRows.length;
        }
        // Every type has the same tabs, so switching type keeps everything entered
        selectType(id) {
            this.interactionTypeId = id;
        }
        onEditorChanged(html) {
            this.bodyHtml = html;
        }
        nextKey() {
            this.rowKey += 1;
            return this.rowKey;
        }
        // The open row's own vee-validate observer (only one row shows its form at a time). Resolves true when no row is open.
        validateOpenActionRow() {
            const openKey = this.editingActionKey;
            const ref = this.$refs.actionRowObserver;
            const observer = Array.isArray(ref) ? ref[0] : ref;
            if (openKey === null || !observer) {
                return Promise.resolve(true);
            }
            return observer.validate().then((valid) => valid && !this.dateError("due-" + openKey));
        }
        // Action item rows — new rows open in the form; saved rows stay in view until Edit.
        // The open row has to be complete (Action, Assignee, Due Date) before another row is added or opened, or it's closed.
        addActionItemRow() {
            this.validateOpenActionRow().then((valid) => {
                if (!valid) {
                    return;
                }
                this.commitOpenActionRow();
                const key = this.nextKey();
                this.actionItemRows = [{ key: key, description: "", assigneeUserId: "", dueDate: "", completed: false, closing: false, isDraft: true }, ...this.actionItemRows];
                this.editingActionKey = key;
            });
        }
        isActionEditing(row) {
            return this.editingActionKey === row.key;
        }
        editActionItemRow(row) {
            if (this.isRowLocked(row)) {
                return;
            }
            this.validateOpenActionRow().then((valid) => {
                if (valid) {
                    this.commitOpenActionRow();
                    this.editingActionKey = this.isActionEditing(row) ? null : row.key;
                }
            });
        }
        // A confirmed row is no longer a draft, so Cancel is only offered while it is first being added
        commitOpenActionRow() {
            const openKey = this.editingActionKey;
            if (openKey === null) {
                return;
            }
            const open = this.actionItemRows.find((row) => row.key === openKey);
            if (open) {
                open.isDraft = false;
            }
        }
        cancelActionItemRow(row) {
            if (!row.isDraft) {
                return;
            }
            this.removeActionItemRow(row);
        }
        assigneeLabel(row) {
            const match = this.assigneeOptions.find((option) => sameId(option.id, row.assigneeUserId));
            return match ? match.value : "";
        }
        actionDueLabel(row) {
            return formatDate(row.dueDate);
        }
        removeActionItemRow(row) {
            if (this.isRowLocked(row)) {
                return;
            }
            if (row.id) {
                this.deletedActionItemIds = [...this.deletedActionItemIds, row.id];
            }
            if (this.editingActionKey === row.key) {
                this.editingActionKey = null;
            }
            this.acceptDate("due-" + row.key);
            this.actionItemRows = this.actionItemRows.filter((x) => x.key !== row.key);
        }
        // Link / attachment rows (no limit on how many)
        addLinkRow() {
            this.linkRows = [...this.linkRows, { key: this.nextKey(), kind: "link", url: "", file: null }];
        }
        openFilePicker() {
            const input = this.$refs.fileInput;
            if (input) {
                input.click();
            }
        }
        onFileSelected(event) {
            const input = event.target;
            const file = input.files && input.files.length ? input.files[0] : null;
            input.value = "";
            if (!file) {
                return;
            }
            const extension = file.name.indexOf(".") > -1 ? file.name.split(".").pop().toLowerCase() : "";
            if (ALLOWED_EXTENSIONS.indexOf(extension) === -1) {
                toaster.ShowError("Only PDF, DOCX, XLSX, PNG or JPEG files can be attached.");
                return;
            }
            if (file.size > MAX_FILE_BYTES) {
                toaster.ShowError("Files must be under 10MB.");
                return;
            }
            this.linkRows = [...this.linkRows, { key: this.nextKey(), kind: "file", url: "", file: file }];
        }
        removeLinkRow(row) {
            if (row.id) {
                this.deletedAttachmentIds = [...this.deletedAttachmentIds, row.id];
            }
            this.linkRows = this.linkRows.filter((x) => x.key !== row.key);
        }
        fileRowName(row) {
            return row.file ? row.file.name : row.fileName || "Attachment";
        }
        isActionItemRowChanged(row) {
            const original = row.original;
            return (!!original &&
                (row.description.trim() !== (original.description || "") ||
                    !sameId(row.assigneeUserId, original.assigneeUserId) ||
                    row.dueDate !== toInputDate(original.dueDate)));
        }
        // Field-level validation (vee-validate), like the Client Details forms. Only the open action item row is
        // rendered as a form, so the first row with a problem is opened (on the Action Items tab) before validating.
        // Save & Close closes the modal; Save keeps it open and goes on editing the saved update.
        save(closeAfter) {
            if (!this.canSave) {
                return;
            }
            const invalidRow = this.actionItemRows.find((row) => this.isRowInvalid(row));
            if (invalidRow) {
                this.extrasTab = "actionItems";
                this.editingActionKey = invalidRow.key;
            }
            this.$nextTick(() => {
                this.validate("updateObserver").then((valid) => {
                    if (valid && !invalidRow && !this.hasDateErrors && !this.isBusy) {
                        this.submit(closeAfter);
                    }
                });
            });
        }
        submit(closeAfter) {
            // Existing rows always count; blank new rows are dropped.
            const filledActionRows = this.filledExtras.actionItems;
            this.submitting = true;
            const model = new ClientUpdateModel();
            if (this.isEdit && this.entry) {
                model.id = this.entry.id;
            }
            model.clientId = this.clientId;
            model.teamId = this.teamId || null;
            model.updateSideId = this.sideId;
            model.interactionTypeId = this.isNotes ? null : this.interactionTypeId;
            model.isUpdatesOnly = this.isUpdatesOnly;
            model.entryDate = this.entryDate;
            model.updateHtml = this.bodyHtml;
            model.attachments = [];
            model.actionItems = [];
            // Save oldest-new first so the row just added (top of the list) gets the latest CreatedOn
            const actionItems = filledActionRows
                .filter((row) => !row.id)
                .sort((a, b) => a.key - b.key)
                .map((row) => {
                const item = new ActionItemModel();
                item.clientId = this.clientId;
                item.description = row.description.trim();
                item.assigneeUserId = row.assigneeUserId;
                item.dueDate = row.dueDate;
                return item;
            });
            // Edited existing items keep everything else (status, completion, links) from the original
            const editedActionItems = filledActionRows
                .filter((row) => !!row.id && this.isActionItemRowChanged(row))
                .map((row) => {
                const item = new ActionItemModel();
                Object.assign(item, row.original);
                item.description = row.description.trim();
                item.assigneeUserId = row.assigneeUserId;
                item.dueDate = row.dueDate;
                return item;
            });
            const deletedAttachmentIds = [...this.deletedAttachmentIds];
            const links = [];
            this.linkRows
                .filter((x) => x.kind === "link")
                .forEach((row) => {
                const url = row.url.trim();
                if (!row.id) {
                    if (url !== "") {
                        links.push({ url: url });
                    }
                    return;
                }
                // No edit endpoint for links: a changed URL is saved as remove + add; a cleared URL is a removal
                if (url !== (row.originalUrl || "")) {
                    deletedAttachmentIds.push(row.id);
                    if (url !== "") {
                        links.push({ url: url });
                    }
                }
            });
            const files = this.linkRows.filter((x) => x.kind === "file" && !!x.file).map((x) => x.file);
            // Completed ticks that differ from the saved status
            const existingRows = filledActionRows.filter((row) => !!row.id);
            const completedActionItemIds = existingRows.filter((row) => row.completed && !this.wasCompleted(row)).map((row) => row.id);
            const reopenedActionItemIds = existingRows.filter((row) => !row.completed && this.wasCompleted(row)).map((row) => row.id);
            const closedActionItemIds = existingRows.filter((row) => row.closing && this.canClose(row)).map((row) => row.id);
            const payload = {
                payLoad: model,
                actionItems: actionItems,
                links: links,
                files: files,
                editedActionItems: editedActionItems,
                deletedActionItemIds: [...this.deletedActionItemIds],
                deletedAttachmentIds: deletedAttachmentIds,
                completedActionItemIds: completedActionItemIds,
                reopenedActionItemIds: reopenedActionItemIds,
                closedActionItemIds: closedActionItemIds,
                closeAfter: closeAfter,
            };
            this.$emit("save", payload);
        }
        close() {
            if (this.isBusy) {
                return;
            }
            this.confirmLeave().then((leave) => {
                if (leave) {
                    this.$emit("close");
                }
            });
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin). The rich body is compared
        // as text so the editor normalising its HTML on load doesn't count as a change.
        unsavedState() {
            return {
                entryDate: this.entryDate,
                sideId: this.sideId,
                interactionTypeId: this.interactionTypeId,
                isUpdatesOnly: this.isUpdatesOnly,
                body: htmlToText(this.bodyHtml),
                actionItems: this.actionItemRows.map((x) => [x.id || null, x.description.trim(), x.assigneeUserId, x.dueDate, x.completed, x.closing]),
                links: this.linkRows.map((x) => [x.id || null, x.kind, x.url.trim(), x.file ? x.file.name : null]),
                deletedActionItemIds: this.deletedActionItemIds,
                deletedAttachmentIds: this.deletedAttachmentIds,
            };
        }
        // Fills the form from a saved update - when Edit opens, and again after each Save that keeps the modal open
        // (rows then carry their new ids, so the next Save edits them instead of adding them again)
        loadEntry(entry) {
            this.actionItemRows.forEach((x) => this.acceptDate("due-" + x.key));
            this.entryDate = toInputDate(entry.entryDate);
            this.sideId = entry.updateSideId ? entry.updateSideId.toString().toLowerCase() : UPDATE_SIDE_CLIENT_ID;
            this.interactionTypeId = entry.interactionTypeId ? entry.interactionTypeId.toString().toLowerCase() : "";
            this.isUpdatesOnly = !!entry.isUpdatesOnly;
            this.bodyHtml = entry.updateHtml || "";
            // Existing action items and attachments load as editable rows
            this.actionItemRows = [...(entry.actionItems || [])].sort(byNewestActionItem).map((item) => ({
                key: this.nextKey(),
                description: item.description || "",
                assigneeUserId: item.assigneeUserId ? item.assigneeUserId.toString() : "",
                dueDate: toInputDate(item.dueDate),
                completed: item.status === ActionItemStatus.Completed,
                closing: false,
                id: item.id.toString(),
                original: item,
            }));
            this.linkRows = (entry.attachments || []).map((attachment) => attachment.mediaFileId
                ? { key: this.nextKey(), kind: "file", url: "", file: null, id: attachment.id.toString(), fileName: attachment.label || "Attachment" }
                : { key: this.nextKey(), kind: "link", url: attachment.url || "", file: null, id: attachment.id.toString(), originalUrl: attachment.url || "" });
            this.editingActionKey = null;
            this.deletedActionItemIds = [];
            this.deletedAttachmentIds = [];
        }
        // Save (not Save & Close) hands back the reloaded update; what it holds now is the unchanged state
        onEntryChanged(entry) {
            if (entry) {
                this.loadEntry(entry);
                this.$nextTick(() => this.markFormClean());
            }
        }
        created() {
            if (this.isEdit && this.entry) {
                this.loadEntry(this.entry);
            }
            else {
                this.sideId = sameId(this.defaultSideId, UPDATE_SIDE_DEVELOPMENT_ID) ? UPDATE_SIDE_DEVELOPMENT_ID : UPDATE_SIDE_CLIENT_ID;
            }
        }
    };
    __decorate([
        Prop({ default: "add" }),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "mode", void 0);
    __decorate([
        Prop({ default: null }),
        __metadata("design:type", typeof (_a = typeof IUpdatesTimelineEntryDTO !== "undefined" && IUpdatesTimelineEntryDTO) === "function" ? _a : Object)
    ], AddUpdateModal.prototype, "entry", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "clientId", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "clientName", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "teamId", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "teamName", void 0);
    __decorate([
        Prop({ default: null }),
        __metadata("design:type", typeof (_b = typeof Date !== "undefined" && Date) === "function" ? _b : Object)
    ], AddUpdateModal.prototype, "clientStartDate", void 0);
    __decorate([
        Prop({ default: UPDATE_SIDE_CLIENT_ID }),
        __metadata("design:type", String)
    ], AddUpdateModal.prototype, "defaultSideId", void 0);
    __decorate([
        Prop({ default: () => [] }),
        __metadata("design:type", Array)
    ], AddUpdateModal.prototype, "interactionTypes", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], AddUpdateModal.prototype, "saving", void 0);
    __decorate([
        Watch("saving"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Boolean]),
        __metadata("design:returntype", void 0)
    ], AddUpdateModal.prototype, "onSavingChanged", null);
    __decorate([
        Watch("entry"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [typeof (_c = typeof IUpdatesTimelineEntryDTO !== "undefined" && IUpdatesTimelineEntryDTO) === "function" ? _c : Object]),
        __metadata("design:returntype", void 0)
    ], AddUpdateModal.prototype, "onEntryChanged", null);
    AddUpdateModal = __decorate([
        Component({
            name: "AddUpdateModal",
            components: {
                EcTextEditor,
                SectionLoader,
            },
        })
    ], AddUpdateModal);
    return AddUpdateModal;
})();
export default AddUpdateModal;
